import React from 'react';
import { Props } from './player-channels';
import useTimeout from '@smart-tv/hooks/use-timeout';
import { AUTO_HIDE_TIMEOUT } from '../player-helpers';
import { useSetAtom } from 'jotai';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { channelsInteractionAtom } from '../atoms';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

const SCROLL_DOWN_HEIGHT = 509;
const SCROLL_UP_HEIGHT = 416;

const usePlayerChannels = (props: Props) => {
  const [autoHideTimeout, setAutoHideTimeout] = React.useState<number>();
  const { open, onClose } = props;
  const [keyPress, setKeyPress] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const setChannelsInteraction = useSetAtom(channelsInteractionAtom);
  const [lastScroll, setLastScroll] = React.useState<number>(0);
  const scrollingRef = React.useRef<HTMLUListElement>(null);
  const rootRef = React.createRef<HTMLDivElement>();

  React.useEffect(() => {
    if (!open) {
      setAutoHideTimeout(undefined);
      setKeyPress(false);
      setIsHovered(false);
      return;
    }

    // In browser mode the controls own the shared auto-hide for both panels.
    if (IS_BROWSER) return;

    if (!autoHideTimeout && !keyPress && !isHovered) {
      setAutoHideTimeout(AUTO_HIDE_TIMEOUT);
    }
  }, [open, autoHideTimeout, keyPress, isHovered]);

  React.useEffect(() => {
    if (open) return;

    setChannelsInteraction(false);
  }, [open, setChannelsInteraction]);

  React.useEffect(() => () => setChannelsInteraction(false), [setChannelsInteraction]);

  React.useEffect(() => {
    if (open) {
      rootRef?.current?.focus();
    }
  }, [open, rootRef]);

  useTimeout(() => {
    handleResetChannels();
  }, autoHideTimeout);

  const handleResetChannels = React.useCallback(() => {
    setAutoHideTimeout(undefined);
    onClose();
  }, [onClose]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!keyPress) {
        setAutoHideTimeout(undefined);
        setKeyPress(true);
      }
      if (IS_BROWSER) {
        setChannelsInteraction(true);
      }
      if (isBackKey(event)) {
        event.stopPropagation();
        onClose();
      }
    },
    [onClose, keyPress, setChannelsInteraction]
  );

  const handleKeyUp = React.useCallback(() => {
    setKeyPress(false);
    if (IS_BROWSER && !isHovered) {
      setChannelsInteraction(false);
    }
  }, [isHovered, setChannelsInteraction]);

  const handleChannelFocus = React.useCallback(
    ({ y }: { y: number }) => {
      if (scrollingRef.current) {
        scrollingRef.current.scrollTo({
          top: y - (lastScroll > y || lastScroll === 0 ? SCROLL_UP_HEIGHT : SCROLL_DOWN_HEIGHT),
          behavior: 'auto'
        });
        setLastScroll(y);
      }
      rootRef?.current?.focus();
    },
    [rootRef, lastScroll, setLastScroll]
  );

  const handleMouseEnter = React.useCallback(() => {
    if (IS_BROWSER) {
      setIsHovered(true);
      setAutoHideTimeout(undefined);
      setChannelsInteraction(true);
    }
  }, [setChannelsInteraction]);

  const handleMouseLeave = React.useCallback(() => {
    if (IS_BROWSER) {
      setIsHovered(false);
      setChannelsInteraction(false);
    }
  }, [setChannelsInteraction]);

  return {
    autoHideTimeout,
    keyPress,
    handleKeyUp,
    handleKeyDown,
    handleMouseEnter,
    handleMouseLeave,
    handleChannelFocus,
    scrollingRef,
    rootRef
  };
};

export default usePlayerChannels;
